import { useMemo } from "react";
import { useForm, useSelector, type StandardSchemaV1Issue } from "@tanstack/react-form";

import { Alert } from "@/primitives/Alert";
import { Modal } from "@/primitives/Modal";
import { Form } from "@/primitives/Form";
import { Field } from "@/primitives/Field";
import { FieldArray } from "@/primitives/FieldArray";
import { Input } from "@/primitives/Input";
import { InputGroup } from "@/primitives/InputGroup";
import { Button } from "@/primitives/Button";
import { FA6Icon } from "@/primitives/FA6Icon";
import { Tooltip } from "@/primitives/Tooltip";
import { CopyButton } from "@/components/CopyButton";
import { SecretInputGroup } from "@/components/SecretInputGroup";
import {
	HttpRequestSnippetTabs,
	secret,
	type HttpRequestDefinition,
} from "@/components/HttpRequestSnippetTabs";
import { api } from "@/shared/api";
import type { ErrorResponse, OAuthClient } from "@/shared/timr";
import {
	createOAuthClientEditSchema,
	createRedirectUriItemSchema,
	GRANT_TYPE_AUTHORIZATION_CODE,
	GRANT_TYPE_CLIENT_CREDENTIALS,
	normalizeRedirectUris,
	splitRedirectUris,
	type OAuthClientEditFormValues,
} from "./oAuthClientSchema";
import styles from "./OAuthClientDetailsDialog.module.css";

export interface OAuthSettings {
	oAuthTokenUrl: string;
	timrApiBaseUrl: string;
}

export type OAuthClientDetailsMode = "view" | "edit";

export interface OAuthClientDetailsDialogProps {
	oAuthClient: OAuthClient;
	mode: OAuthClientDetailsMode;
	apiFeatureAllowed: boolean;
	oAuthSettings: OAuthSettings;
	onEditRequest: () => void;
	/** Editing ended — cancelled, or saved with the updated client. The owner decides where to go from here. */
	onEditFinished: (saved?: OAuthClient) => void;
	onDelete: (oAuthClient: OAuthClient) => void;
}

export function OAuthClientDetailsDialog({
	oAuthClient,
	mode,
	apiFeatureAllowed,
	oAuthSettings,
	onEditRequest,
	onEditFinished,
	onDelete,
}: OAuthClientDetailsDialogProps) {
	const editing = mode === "edit";

	const usesAuthorizationCode = oAuthClient.authorizationGrantTypes === GRANT_TYPE_AUTHORIZATION_CODE;
	const usesClientCredentials = oAuthClient.authorizationGrantTypes === GRANT_TYPE_CLIENT_CREDENTIALS;

	const schema = useMemo(() => createOAuthClientEditSchema(usesAuthorizationCode), [usesAuthorizationCode]);
	const redirectUriItemSchema = useMemo(() => createRedirectUriItemSchema(), []);

	const defaultValues = useMemo(
		(): OAuthClientEditFormValues => ({
			oAuthClientName: oAuthClient.oAuthClientName,
			redirectUris: splitRedirectUris(oAuthClient.redirectUris),
		}),
		[oAuthClient],
	);

	/** How to obtain a token with these credentials — only shown for the client-credentials flow. */
	const tokenRequest = useMemo(
		(): HttpRequestDefinition => ({
			method: "POST",
			url: oAuthSettings.oAuthTokenUrl,
			headers: { "Content-Type": "application/x-www-form-urlencoded" },
			form: {
				grant_type: "client_credentials",
				client_id: oAuthClient.oAuthClientId,
				client_secret: secret(oAuthClient.oAuthClientSecret, "client_secret"),
				scope: oAuthClient.scopes,
			},
		}),
		[oAuthClient, oAuthSettings],
	);

	const form = useForm({
		defaultValues,
		validators: {
			onMount: schema,
			onChange: schema,
			onSubmitAsync: async ({ value }) => {
				try {
					const updated = await api("/admin/updateOAuthClient.json").post({
						id: oAuthClient.id,
						oAuthClientName: value.oAuthClientName.trim(),
						authorizationGrantTypes: oAuthClient.authorizationGrantTypes,
						redirectUris: usesAuthorizationCode ? normalizeRedirectUris(value.redirectUris).join("\n") : "",
					});
					// adopt the server-normalized values so a later edit starts from what was saved
					form.reset({
						oAuthClientName: updated.oAuthClientName,
						redirectUris: splitRedirectUris(updated.redirectUris),
					});
					onEditFinished(updated);
				} catch (response: unknown) {
					const fieldErrors = (response as ErrorResponse).responseJSON?.fieldErrors;
					if (fieldErrors != null) {
						return {
							fields: Object.fromEntries(fieldErrors.map((e) => [e.field, e.message])),
						};
					}
				}
			},
		},
	});

	const submitted = useSelector(form.store, (state) => state.submissionAttempts > 0);

	// Show an error only after the field was blurred or a submit was attempted — never while typing.
	// `meta.errors` holds Standard Schema issues (`{message}`) from Zod and the server's plain strings.
	const errorFor = (meta: { isBlurred: boolean; errors: readonly unknown[] }): string | null => {
		if (!meta.isBlurred && !submitted) return null;
		const error = meta.errors[0];
		if (typeof error === "string") return error;
		const message = (error as StandardSchemaV1Issue)?.message;
		return typeof message === "string" ? message : null;
	};

	const cancelEdit = () => {
		form.reset();
		onEditFinished();
	};

	return (
		<Modal.Content>
			<Modal.Header>
				<Modal.Title>
					{messages[editing ? "oAuthClient.header.edit" : "oAuthClient.header.show"]}
				</Modal.Title>
			</Modal.Header>

			<Form
				orientation="horizontal"
				noValidate /* block native form validation */
				/* Capture phase — see changePasswordModal.tsx for why Base UI's Form must not own submit. */
				onSubmitCapture={(event) => {
					event.preventDefault();
					event.stopPropagation();
					void form.handleSubmit();
				}}
			>
				<Modal.Body>
					<form.Field name="oAuthClientName">
						{(field) => {
							const error = errorFor(field.state.meta);
							return (
								<Field.Root name="oAuthClientName" invalid={editing && error != null}>
									<Field.Label>{messages["admin.oAuthClient.name"]}</Field.Label>
									{editing ? (
										<>
											<Tooltip.Provider>
												<InputGroup.Root>
													<Input
														autoFocus
														maxLength={255}
														value={field.state.value}
														onChange={(event) => field.handleChange(event.target.value)}
														onBlur={field.handleBlur}
													/>
													<CopyButton text={field.state.value} render={<InputGroup.Button />} />
												</InputGroup.Root>
											</Tooltip.Provider>
											{error != null && <Field.Error match>{error}</Field.Error>}
										</>
									) : (
										<SecretInputGroup value={oAuthClient.oAuthClientName} canShow={false} />
									)}
								</Field.Root>
							);
						}}
					</form.Field>

					<Field.Root>
						<Field.Label>{messages["oAuthClient.oAuthClientId"]}</Field.Label>
						<SecretInputGroup value={oAuthClient.oAuthClientId} canShow={false} />
					</Field.Root>

					<Field.Root>
						<Field.Label>{messages["oAuthClient.oAuthClientSecret"]}</Field.Label>
						<SecretInputGroup value={oAuthClient.oAuthClientSecret} />
					</Field.Root>

					<Field.Root>
						<Field.Label>{messages["oAuthClient.oAuthGrantType"]}</Field.Label>
						{/* the wire value (e.g. "client_credentials"), so the copy button yields what a request needs */}
						<SecretInputGroup value={oAuthClient.authorizationGrantTypes} canShow={false} />
					</Field.Root>

					{usesAuthorizationCode && (
						editing ? (
							<form.Field name="redirectUris" mode="array">
								{(field) => {
									const error = errorFor(field.state.meta);
									return (
										<FieldArray.Root
											items={field.state.value}
											onItemsChange={(items) => field.handleChange(items)}
											schema={redirectUriItemSchema}
										>
											<FieldArray.Items>
												{({ value, index, setValue, remove, validate, isOnly, onKeyDown }) => (
													// display timing stays with Base UI: the row's message appears on blur, not while typing
													<Field.Root name={`redirectUris-${index}`} validationMode="onBlur" validate={validate}>
														<Field.Label>{index === 0 ? messages["oAuthClient.redirectUris"] : ""}</Field.Label>
														<InputGroup.Root>
															<InputGroup.Addon square>
																<FA6Icon icon="link" variant="regular" />
															</InputGroup.Addon>
															<Input
																aria-label={`${messages["oAuthClient.redirectUris"]} ${index + 1}`}
																placeholder="https://"
																value={value}
																onChange={(event) => setValue(event.target.value)}
																onBlur={field.handleBlur}
																onKeyDown={onKeyDown}
															/>
															<InputGroup.Button
																aria-label={messages["dialog.delete"]}
																// the last remaining row must stay — the flow needs at least one redirect URL
																disabled={isOnly}
																onClick={remove}
															>
																<FA6Icon icon="trash-can" variant="light" />
															</InputGroup.Button>
														</InputGroup.Root>
														{/* childless: renders whatever the row's validate returned */}
														<Field.Error />
													</Field.Root>
												)}
											</FieldArray.Items>
											<Field.Root invalid={error != null}>
												<Field.Label />
												<div>
													<FieldArray.Add variant="secondary" requireValid>
														<FA6Icon icon="plus" variant="light" className="position-left" />
														{messages["oAuthClient.button.add"]}
													</FieldArray.Add>
												</div>
												<Field.Error match={error != null}>{error}</Field.Error>
											</Field.Root>
										</FieldArray.Root>
									);
								}}
							</form.Field>
						) : (
							<Field.Root>
								<Field.Label>{messages["oAuthClient.redirectUris"]}</Field.Label>
								<div className={styles.readOnlyBox}>
									{defaultValues.redirectUris.map((value, index) => (
										<InputGroup.Root key={index}>
											<InputGroup.Addon square>
												<FA6Icon icon="link" variant="regular" />
											</InputGroup.Addon>
											<Input
												readOnly
												aria-label={`${messages["oAuthClient.redirectUris"]} ${index + 1}`}
												value={value}
											/>
										</InputGroup.Root>
									))}
								</div>
							</Field.Root>
						)
					)}

					<Field.Root>
						<Field.Label>{messages["oAuthClient.scopes"]}</Field.Label>
						<SecretInputGroup value={oAuthClient.scopes} canShow={false} />
					</Field.Root>

					<div className="oauth-client-modal-divider" />

					<Field.Root>
						<Field.Label>{messages["authServer.oAuthTokenUrl"]}</Field.Label>
						<SecretInputGroup value={oAuthSettings.oAuthTokenUrl} canShow={false} />
					</Field.Root>

					<Field.Root>
						<Field.Label>{messages["timrApi.baseUrl"]}</Field.Label>
						<SecretInputGroup value={oAuthSettings.timrApiBaseUrl} canShow={false} />
					</Field.Root>

					{usesClientCredentials && (
						<>
							<p>{messages["oAuthInfo.instructions"]}:</p>
							<HttpRequestSnippetTabs request={tokenRequest} className="mb-15" />
							<Alert variant="danger" iconPosition="left">
								{messages["oauth.permission.warning"]}
							</Alert>
						</>
					)}
					{usesAuthorizationCode && (
						<Alert variant="info" iconPosition="left">
							{messages["oauth.authorizationCode.permission.info"]}
						</Alert>
					)}
				</Modal.Body>

				<Modal.Footer>
					<Button
						type="button"
						variant="destructiveOutline"
						style={{ marginRight: "auto" }}
						onClick={() => onDelete(oAuthClient)}
					>
						{messages["dialog.delete"]}
					</Button>
					{editing ? (
						<>
							<Button type="button" variant="secondary" onClick={cancelEdit}>
								{messages["dialog.cancel"]}
							</Button>
							<form.Subscribe selector={(state) => state.isSubmitting}>
								{(isSubmitting) => (
									<Button type="submit" disabled={isSubmitting}>
										{isSubmitting && (
											<FA6Icon icon="spinner-third" variant="regular" className="spinner mr-5" />
										)}
										{messages["dialog.save"]}
									</Button>
								)}
							</form.Subscribe>
						</>
					) : (
						<>
							<Modal.Close render={<Button variant="secondary" type="button" />}>
								{messages["dialog.close"]}
							</Modal.Close>
							{apiFeatureAllowed && (
								<Button type="button" onClick={onEditRequest}>
									{messages["oAuthClient.context.edit"]}
								</Button>
							)}
						</>
					)}
				</Modal.Footer>
			</Form>
		</Modal.Content>
	);
}
